/**
 * The third-party side of the catalogue (`specs/M6-A4-integrations.md`).
 *
 * Everything an integration block needs that is **not** a monday domain write lives behind this
 * port: one outbound HTTP call, made by the worker, against a credential the account has stored
 * on the Connections page. `packages/shared` therefore stays free of `fetch`, of hosts and of
 * secrets — a block only ever says *what* to send and *through which app*.
 *
 * Two rules this file exists to enforce:
 *
 * 1. **No credential ever reaches a block.** A block passes an `IntegrationApp` and a payload;
 *    the worker resolves the account's connection, decrypts the token and makes the call. A
 *    block cannot read a token even by accident, so a token cannot end up in `node_results`,
 *    in the run log or in a rendered sentence.
 * 2. **Missing authorisation is a first-class outcome, not a crash.** `AutomationNotConnected`
 *    is thrown when no connection exists; the engine parks the automation `not_connected` with
 *    monday's own state value — the state 5 production rows are already in
 *    (`discovery/automations/api/catalog.md` §1).
 */
import { z } from 'zod';
/** The apps this account actually uses (`discovery/integrations.md`) plus the generic sender. */
export const INTEGRATION_APPS = ['slack', 'gmail', 'googleCalendar', 'webhooks'];
export const integrationAppSchema = z.enum(INTEGRATION_APPS);
/**
 * monday's own `connection` slot, verbatim from the 8 production `7459` nodes:
 * `{authProvider, connectionId, appId, invalid}`. `connectionId` is an **integer credential
 * id**, never a secret (`catalog.md` §1: "`auth.<app>` holds an integer credential id").
 * It is preserved on import so a re-import can still diff, and it is never used to authenticate
 * — our own connection is looked up by app, per account.
 */
export const connectionRefSchema = z
    .object({
    authProvider: z.string().optional(),
    connectionId: z.union([z.number(), z.string()]).optional(),
    appId: z.union([z.number(), z.string()]).optional(),
    invalid: z.boolean().optional(),
})
    .default({});
/** A Slack channel or user as monday's picker stores it: `{value, title}`, or `null`. */
export const slackTargetSchema = z
    .union([
    z.null(),
    z.object({
        value: z.string().min(1),
        title: z.string().optional(),
        invalid: z.boolean().optional(),
    }),
])
    .default(null);
/** Static recipe 174's `email` slot: `{subject, body, invalid}` (`configurationFields.email`). */
export const emailBodySchema = z
    .object({
    subject: z.string().default(''),
    body: z.string().default(''),
    invalid: z.boolean().optional(),
})
    .default({ subject: '', body: '' });
/**
 * No connection for the app. The engine turns this into monday's `not_connected` state rather
 * than into a failure, so the automation is visibly waiting for an admin instead of silently
 * burning its failure budget.
 */
export class AutomationNotConnected extends Error {
    app;
    code = 'not_connected';
    constructor(app) {
        super(`no ${app} connection: an admin must connect ${app} on Admin ▸ Connections`);
        this.app = app;
        this.name = 'AutomationNotConnected';
    }
}
/** The block is configured but a required slot is empty — e.g. a Slack recipe with no channel. */
export class AutomationNotConfigured extends Error {
    slot;
    code = 'not_configured';
    constructor(slot) {
        super(`automation slot "${slot}" is not configured`);
        this.slot = slot;
        this.name = 'AutomationNotConfigured';
    }
}
export const isNotConnected = (error) => error instanceof AutomationNotConnected ||
    (typeof error === 'object' && error !== null && error.code === 'not_connected');
export const isNotConfigured = (error) => error instanceof AutomationNotConfigured ||
    (typeof error === 'object' && error !== null && error.code === 'not_configured');
//# sourceMappingURL=integrations.js.map